/**
 * UploadOverlay — wraps content area with a drop overlay for file uploads.
 *
 * Uses react-dropzone for drag-from-desktop detection and displays
 * upload progress. Files upload immediately on drop.
 */

import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'

interface UploadOverlayProps {
  children: React.ReactNode
  disabled: boolean
  onUpload: (
    files: File[],
    onProgress: (pct: number) => void,
    onSpeed?: (mbps: number) => void,
  ) => Promise<void>
  /** External upload-in-progress flag (e.g. from button upload) */
  uploading?: boolean
  /** External upload progress 0-100 (e.g. from button upload) */
  uploadProgress?: number
  /** External upload speed in MB/s (e.g. from button upload) */
  uploadSpeedMBps?: number
}

export default function UploadOverlay({
  children, disabled, onUpload,
  uploading: externalUploading,
  uploadProgress: externalProgress,
  uploadSpeedMBps: externalSpeed,
}: UploadOverlayProps) {
  const [dropUploading, setDropUploading] = useState(false)
  const [dropProgress, setDropProgress] = useState(0)
  const [dropSpeed, setDropSpeed] = useState(0)

  const uploading = dropUploading || (externalUploading ?? false)
  const progress = dropUploading ? dropProgress : (externalProgress ?? 0)
  const speedMBps = dropUploading ? dropSpeed : (externalSpeed ?? 0)

  const onDrop = useCallback(async (accepted: File[]) => {
    if (accepted.length === 0 || disabled) return
    setDropUploading(true)
    setDropProgress(0)
    setDropSpeed(0)
    try {
      await onUpload(accepted, setDropProgress, setDropSpeed)
    } finally {
      setDropUploading(false)
      setDropProgress(0)
      setDropSpeed(0)
    }
  }, [disabled, onUpload])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled: disabled || uploading,
  })

  return (
    <div {...getRootProps()} className="relative flex-1 flex flex-col min-h-0">
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <div className="fm-upload-overlay">
          <div className="flex flex-col items-center gap-3">
            <div className="p-4 bg-[var(--rq-blue)]/20 rounded-full border border-[var(--rq-blue)]/30">
              <Upload className="w-10 h-10 text-[var(--rq-blue)]" />
            </div>
            <p className="text-lg font-semibold text-[var(--rq-text)]">Drop files to upload</p>
            <p className="text-sm text-[var(--rq-text-soft)]">Release to add to current folder</p>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="absolute top-0 left-0 right-0 z-20 px-4 py-2 bg-black/60 backdrop-blur-sm">
          <div className="flex justify-between text-xs text-[var(--rq-text-soft)] mb-1">
            <span>Uploading...</span>
            <span className="tabular-nums">
              {speedMBps > 0 && (
                <span className="text-[var(--rq-text)] font-medium mr-3">
                  {speedMBps.toFixed(1)} MB/s
                </span>
              )}
              {progress}%
            </span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-1.5">
            <div
              className="bg-[var(--rq-blue)] h-1.5 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
